/**
 * bundleDeck — anima la pila di carte in una course-card --bundle.
 *
 * Ogni INTERVAL_MS la carta in cima (pos-0) esce verso l'alto (--exiting),
 * poi dopo EXIT_MS viene spostata in fondo e tutte le altre avanzano di un posto.
 *
 * Uso nel template:
 *   <div class="ik-course-card__bundle-deck" x-data="bundleDeck(N)">
 *     <div class="ik-course-card__bundle-card" :class="cardClass(0)">...</div>
 *     <div class="ik-course-card__bundle-card" :class="cardClass(1)">...</div>
 *     <!-- ... -->
 *   </div>
 */

const INTERVAL_MS = 2500;
const EXIT_MS     = 400;

export function registerBundleDeck(Alpine) {
    Alpine.data('bundleDeck', (count = 2) => ({
        // positions[i] = posizione CSS corrente della carta i (0 = cima, 1 = media, 2 = fondo)
        positions: Array.from({ length: count }, (_, i) => i),
        exiting: -1, // indice carta in uscita
        _timer: null,
        _mq: null,

        _isMobile() {
            return this._mq ? this._mq.matches : window.matchMedia('(max-width: 767.98px)').matches;
        },

        init() {
            if (count < 2) return;
            // Misura la carta più alta e imposta l'altezza del deck
            this.$nextTick(() => {
                const cards = this.$el.querySelectorAll('.ik-course-card__bundle-card');
                let maxH = 0;
                cards.forEach(c => { maxH = Math.max(maxH, c.scrollHeight); });
                if (maxH > 0) {
                    this.$el.style.height = (maxH + 32) + 'px';
                }
            });

            this._mq = window.matchMedia('(max-width: 767.98px)');

            // Su mobile: autorotate subito
            if (this._isMobile()) {
                this.startRotation();
            }

            // Hover su desktop
            const card = this.$el.closest('.ik-course-card');
            if (card) {
                this._onEnter = () => { if (!this._isMobile()) this.startRotation(); };
                this._onLeave = () => { if (!this._isMobile()) this.stopRotation(); };
                card.addEventListener('mouseenter', this._onEnter);
                card.addEventListener('mouseleave', this._onLeave);
            }

            // Gestione resize: passaggio mobile ↔ desktop
            this._onMqChange = (e) => {
                if (e.matches) {
                    // passato a mobile → autorotate
                    this.startRotation();
                } else {
                    // passato a desktop → ferma, aspetta hover
                    this.stopRotation();
                }
            };
            this._mq.addEventListener('change', this._onMqChange);
        },

        destroy() {
            clearInterval(this._timer);
            if (this._mq && this._onMqChange) {
                this._mq.removeEventListener('change', this._onMqChange);
            }
            const card = this.$el.closest('.ik-course-card');
            if (card) {
                if (this._onEnter) card.removeEventListener('mouseenter', this._onEnter);
                if (this._onLeave) card.removeEventListener('mouseleave', this._onLeave);
            }
        },

        startRotation() {
            if (count < 2 || this._timer) return;
            this._rotate();
            this._timer = setInterval(() => this._rotate(), INTERVAL_MS);
        },

        stopRotation() {
            clearInterval(this._timer);
            this._timer = null;
        },

        _rotate() {
            const topIdx = this.positions.indexOf(0);
            const cards = this.$el.querySelectorAll('.ik-course-card__bundle-card');
            const exitingCard = cards[topIdx];

            // Blocca l'altezza corrente e rimuove bottom per evitare che la carta
            // si allunghi quando animiamo top (top+bottom insieme cambiano l'altezza)
            if (exitingCard) {
                const h = exitingCard.offsetHeight;
                exitingCard.style.bottom = 'auto';
                exitingCard.style.height = h + 'px';
                exitingCard.style.zIndex = '10';

                // Fase 1: sale senza fade (150ms)
                exitingCard.style.transition = 'top 150ms ease-out';
                exitingCard.style.top = '-24px';

                // Fase 2: continua a salire e fa fade out (altri 250ms)
                setTimeout(() => {
                    exitingCard.style.transition = 'top 250ms ease-in, opacity 250ms ease-in';
                    exitingCard.style.top = '-80px';
                    exitingCard.style.opacity = '0';
                }, 150);
            }

            setTimeout(() => {
                // Riposiziona in fondo istantaneamente (transition: none)
                if (exitingCard) {
                    exitingCard.style.transition = 'none';
                    exitingCard.style.top = '';
                    exitingCard.style.bottom = '';
                    exitingCard.style.height = '';
                    exitingCard.style.opacity = '';
                    exitingCard.style.zIndex = '';
                }

                // Aggiorna le posizioni Alpine
                this.positions = this.positions.map((pos, i) => {
                    if (i === topIdx) return count - 1;
                    return pos - 1;
                });

                // Riabilita le transizioni CSS al frame successivo
                requestAnimationFrame(() => {
                    if (exitingCard) exitingCard.style.transition = '';
                });
            }, EXIT_MS);
        },

        cardClass(idx) {
            const pos = this.positions[idx];
            return `ik-course-card__bundle-card--pos-${pos}`;
        },
    }));
}
